import { useEffect, useRef } from "react";

import {
    getAppUrls,
    IAppUrls,
    parseNamespace,
} from "@formant/core/common/app-urls";
import { namespace } from "@formant/core/common/config";
import { fork } from "@formant/core/common/fork";

import { authStore, organizationStore } from "../../stores";
import {
    coherenceUrlFor,
    fetchCoherenceRedirectEnabled,
    ILocationLike,
    legacyPreferenceFromSearch,
    shouldRedirectToCoherence,
} from "../coherenceRedirect/coherenceRedirect";

const keepLegacyStorageKey = "formant.useLegacy";

function appUrlsForNamespace(): IAppUrls | undefined {
    try {
        return getAppUrls(parseNamespace(namespace));
    } catch {
        return undefined;
    }
}

// sessionStorage throws in some private-browsing modes; treat that as "no
// preference" rather than failing the page.
function readKeepLegacy(): boolean {
    try {
        return sessionStorage.getItem(keepLegacyStorageKey) === "true";
    } catch {
        return false;
    }
}

function writeKeepLegacy(keepLegacy: boolean): void {
    try {
        if (keepLegacy) {
            sessionStorage.setItem(keepLegacyStorageKey, "true");
        } else {
            sessionStorage.removeItem(keepLegacyStorageKey);
        }
    } catch {
        // Nothing to persist to; the choice lasts for this page load only.
    }
}

async function redirectIfEnabled(
    { coherence, platform }: IAppUrls,
    landing: ILocationLike,
): Promise<void> {
    const redirect = await shouldRedirectToCoherence({
        locations: [landing, window.location],
        keepLegacy: readKeepLegacy(),
        readFlag: async () =>
            fetchCoherenceRedirectEnabled(platform, await authStore.token()),
    });
    if (redirect) {
        // replace() so the back button doesn't return to a page that would
        // redirect again.
        window.location.replace(coherenceUrlFor(coherence, landing));
    }
}

/**
 * Sends signed-in users of an organization with `coherenceRedirectEnabled` to
 * Coherence. A device or alert link opens that device there; any other page
 * opens Coherence's home. The session carries over through the shared
 * `.formant.io` auth cookie. `?useLegacy=true` keeps the tab on app.formant;
 * `?useLegacy=false` clears that choice.
 */
export function useCoherenceRedirect(): void {
    const organization = organizationStore.authenticated();
    // The router rewrites the URL once it takes over, so keep the URL the user
    // landed on for the escape hatch, the exemption checks and the target page.
    const landing = useRef<ILocationLike>({
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
    });
    const checked = useRef(false);

    useEffect(() => {
        const keepLegacy = legacyPreferenceFromSearch(landing.current.search);
        if (keepLegacy !== undefined) {
            writeKeepLegacy(keepLegacy);
        }
    }, []);

    useEffect(() => {
        const appUrls = appUrlsForNamespace();
        if (checked.current || !organization || !appUrls) {
            return;
        }
        checked.current = true;
        fork(redirectIfEnabled(appUrls, landing.current));
    }, [organization]);
}
